import { createSelector, createSlice } from "@reduxjs/toolkit";
import { Buffer } from "buffer";
import { forIn } from "lodash";
import isEqual from "lodash/isEqual";
import axios from "../axios";
import axiosInstanceWithoutInterceptor from "../axiosWithoutInterceptor";
import getConfig from "../config";
import { EcmRole } from "../features/navigation/Subscription";
import toast from "../hooks/toast";
import { logger } from "../shared/Logger";
import {
    buildApiV2Point5UrlWithQuery,
    buildApiV3Url,
    buildApiV3UrlWithQuery,
    getStoredItem,
    setStoredItem,
    transformExemptionData,
    validateEmail,
} from "../shared/Utils";
import { errorResponse } from "../shared/responseUtils";
import {
    PREF_KEY_PENDING_CERTIFICATE_EMAIL,
    PREF_KEY_OPT_IN_ESCALATED_NOTIFICATIONS,
    PREF_KEY_OPT_IN_VALIDATION_NOTIFICATIONS,
} from "../shared/services/preferencesService";
import {
    AuiWorkflowTaskCategory,
    setAuiWorkflowData,
    updateAuiWorkflowTask,
} from "./auiWorkflowSlice";

// Config is retrieved lazily inside functions to support runtime configuration

const initialState = {
    isLoading: false,
    settingData: [],
    isLoadingCertificateRequest: false,
    certificateRequest: [],
    isLoadingAutoRequestCoverLetter: false,
    autoRequestCoverLetter: null,
    isSavingSettings: false,
    isFetchingLogo: false,
    isSavingLogo: false,
    isDeletingLogo: false,
    companyLogo: { fileType: "", data: "", fileName: "" },
    isCompanyLogoPresent: false,
    isLoadingCertificateRequestForm: false,
    isLoadingSaveMatrixSetting: false,
    certificateFormsDialogOpen: false,
    requestingCertificateFormsDialogOpen: false,
    matrixSetting: {},
    resetMatrixSetting: {},
    isSignatureLoading: false,
    signatureData: {},
    collectionRegions: {},
    collectionInfo: {},
    isLoadingDocumentType: false,
    documentTypeInfo: {},
    isLoadingCollectionRegions: false,
    isOutgoingMatrixSet: true,
    matrixCheckLoading: false,
    requestCertificateRegions: { regionId: "", regionName: "" },
    requestCertificateReasons: { reasonId: "", reasonName: "" },
    sendingCertificateRegions: { regionId: "", regionName: "" },
    sendingCertificateReasons: { reasonId: "", reasonName: "" },
    defaultExist: false,
    isRequestingAvalaraDefault: false,
    isSendingAvalaraDefault: false,
    noDataType: "",
    corporateEmail: "",
    invalidReplacementEmail: "",
    equipmentDescription: "",
    isLoadingMatrixFile: false,
    isSavingMatrixFile: false,
    matrixFile: "",
    isSendingStaticDocument: false,
    page: {
        data: [],
        paginate: {
            pageNumber: 0,
            startIndex: -1,
            endIndex: 0,
            rowsPerPage: 10,
            totalRecords: 0,
        },
    },
    publicCertExpressToken: "",
    sortColumn: ["region.name", true],
    isRemoveRegionReasonLoading: false,
    isSignChange: false,
    addNewSignature: false,
    userInfo: {
        id: null,
        subjectId: null,
        userName: null,
        firstName: null,
        lastName: null,
        email: null,
        isActive: null,
        userRoles: null,
    },
    isLoadingUserInfo: false,
    isLoadingupdateRole: false,
    certExpressSettings: {
        savedState: {},
        currentState: {},
    },
    pendingCertificateEmailPreference: {
        enabled: false,
        email: "",
    },
    isLoadingNotificationPreferences: false,
    escalationEmailPreference: {
        enabled: false,
        email: "",
    },
    validationEmailPreference: {
        enabled: false,
        email: "",
    },
    preferencesLoaded: false,
    previousPath: "",
};

export const settingsSlice = createSlice({
    name: "setting",
    initialState,
    reducers: {
        setLoading: (state, action) => {
            state.isLoading = action.payload;
        },
        setSetting: (state, action) => {
            const prevState = state.settingData;
            state.settingData = action.payload;
            if (!isEqual(prevState, action.payload))
                setStoredItem("generalSettings", action.payload);
        },
        setIsLoadingCertificateRequest: (state, action) => {
            state.isLoadingCertificateRequest = action.payload;
        },
        setCertificateRequest: (state, action) => {
            state.certificateRequest = action.payload;
        },
        setIsLoadingAutoRequestCoverLetter: (state, action) => {
            state.isLoadingAutoRequestCoverLetter = action.payload;
        },
        setAutoRequestCoverLetter: (state, action) => {
            state.autoRequestCoverLetter = action.payload;
        },
        setCompanyLogo: (state, action) => {
            state.companyLogo = action.payload;
        },
        setIsSavingSettings: (state, action) => {
            state.isSavingSettings = action.payload;
        },
        setIsFetchingLogo: (state, action) => {
            state.isFetchingLogo = action.payload;
        },
        setIsSavingLogo: (state, action) => {
            state.isSavingLogo = action.payload;
        },
        setIsDeletingLogo: (state, action) => {
            state.isDeletingLogo = action.payload;
        },
        setIsCompanyLogoPresent: (state, action) => {
            state.isCompanyLogoPresent = action.payload;
        },
        setIsLoadingCertificateRequestForm: (state, action) => {
            state.isLoadingCertificateRequestForm = action.payload;
        },

        setIsLoadingSaveMatrixSetting: (state, action) => {
            state.isLoadingSaveMatrixSetting = action.payload;
        },

        setMatrixSetting: (state, action) => {
            state.matrixSetting = action.payload;
        },
        setResetMatrixSetting: (state, action) => {
            state.resetMatrixSetting = action.payload;
        },
        setCertificateFormsDialogOpen: (state, action) => {
            state.certificateFormsDialogOpen = action.payload;
        },
        setRequestingCertificateFormsDialogOpen: (state, action) => {
            state.requestingCertificateFormsDialogOpen = action.payload;
        },
        setCollectionRegions: (state, action) => {
            state.collectionRegions = action.payload;
        },
        setIsLoadingCollectionRegion: (state, action) => {
            state.isLoadingCollectionRegions = action.payload;
        },
        setMatrixCheckLoading: (state, action) => {
            state.matrixCheckLoading = action.payload;
        },
        setIsOutgoingMatrixSet: (state, action) => {
            state.isOutgoingMatrixSet = action.payload;
        },
        setCollectionInfo: (state, action) => {
            state.collectionInfo = action.payload;
        },
        setLoadingDocumentType: (state, action) => {
            state.isLoadingDocumentType = action.payload;
        },
        setDocumentTypeInfo: (state, action) => {
            state.documentTypeInfo = action.payload;
        },
        setPage: (state, action) => {
            state.page = action.payload;
        },
        setPageToNull: (state, action) => {
            state.page = {
                data: [],
                paginate: {
                    pageNumber: 0,
                    startIndex: -1,
                    endIndex: 0,
                    rowsPerPage: action?.payload,
                    totalRecords: 0,
                },
            };
        },
        setNoDataType: (state, action) => {
            state.noDataType = action.payload;
        },
        setRequestCertificateRegions: (state, action) => {
            state.requestCertificateRegions = action.payload;
        },
        setRequestCertificateReasons: (state, action) => {
            state.requestCertificateReasons = action.payload;
        },

        setSendingCertificateRegions: (state, action) => {
            state.sendingCertificateRegions = action.payload;
        },
        setSendingCertificateReasons: (state, action) => {
            state.sendingCertificateReasons = action.payload;
        },
        setIsRequestingAvalaraDefault: (state, action) => {
            state.isRequestingAvalaraDefault = action.payload;
        },
        setIsSendingAvalaraDefault: (state, action) => {
            state.isSendingAvalaraDefault = action.payload;
        },
        setSignatureLoading: (state, action) => {
            state.isSignatureLoading = action.payload;
        },
        setSignatureData: (state, action) => {
            state.signatureData = action.payload;
        },
        setCorporateEmail: (state, action) => {
            state.corporateEmail = action.payload;
        },
        setInvalidReplacementEmailInput: (state, action) => {
            state.invalidReplacementEmail = action.payload;
        },
        setEquipmentDescription: (state, action) => {
            state.equipmentDescription = action.payload;
        },
        setLoadingMatrixFile: (state, action) => {
            state.isLoadingMatrixFile = action.payload;
        },
        setSavingMatrixFile: (state, action) => {
            state.isSavingMatrixFile = action.payload;
        },
        setMatrixFile: (state, action) => {
            state.matrixFile = action.payload;
        },
        setIsSendingStaticDocument: (state, action) => {
            state.isSendingStaticDocument = action.payload;
        },
        setDefaultExist: (state, action) => {
            state.defaultExist = action.payload;
        },
        setPublicCertExpressToken: (state, action) => {
            state.publicCertExpressToken = action.payload;
        },
        setSortColumn: (state, action) => {
            state.sortColumn = action.payload;
        },
        setIsRemoveRegionReasonLoading: (state, action) => {
            state.isRemoveRegionReasonLoading = action.payload;
        },
        setIsSignChange: (state, action) => {
            state.isSignChange = action.payload;
        },
        setAddNewSignature: (state, action) => {
            state.addNewSignature = action.payload;
        },
        setUserInfo: (state, action) => {
            state.userInfo = action.payload;
        },
        setIsLoadingUserInfo: (state, action) => {
            state.isLoadingUserInfo = action.payload;
        },
        setIsLoadingupdateRole: (state, action) => {
            state.isLoadingupdateRole = action.payload;
        },
        setSavedCertExpressSettings: (state, action) => {
            state.certExpressSettings.savedState = action.payload;
        },
        setCurrentCertExpressSettings: (state, action) => {
            state.certExpressSettings.currentState = action.payload;
        },
        setPendingCertificateEmailPreference: (state, action) => {
            state.pendingCertificateEmailPreference = action.payload;
        },
        setIsLoadingNotificationPreferences: (state, action) => {
            state.isLoadingNotificationPreferences = action.payload;
        },
        setEscalationEmailPreference: (state, action) => {
            state.escalationEmailPreference = action.payload;
        },
        setValidationEmailPreference: (state, action) => {
            state.validationEmailPreference = action.payload;
        },
        setPreferencesLoaded: (state, action) => {
            state.preferencesLoaded = action.payload;
        },
        setPreviousPath: (state, action) => {
            state.previousPath = action.payload;
        },
    },
});

export const {
    setLoading,
    setSetting,
    setIsLoadingCertificateRequest,
    setCertificateRequest,
    setIsLoadingAutoRequestCoverLetter,
    setAutoRequestCoverLetter,
    setIsSavingSettings,
    setIsFetchingLogo,
    setIsSavingLogo,
    setIsDeletingLogo,
    setCompanyLogo,
    setIsCompanyLogoPresent,
    setIsLoadingCertificateRequestForm,
    setIsLoadingSaveMatrixSetting,
    setCertificateFormsDialogOpen,
    setRequestingCertificateFormsDialogOpen,
    setPage,
    setPageToNull,
    setNoDataType,
    setRequestCertificateRegions,
    setRequestCertificateReasons,
    setSendingCertificateRegions,
    setSendingCertificateReasons,
    setIsRequestingAvalaraDefault,
    setIsSendingAvalaraDefault,
    setMatrixSetting,
    setResetMatrixSetting,
    setIsLoadingCollectionRegion,
    setMatrixCheckLoading,
    setIsOutgoingMatrixSet,
    setCollectionRegions,
    setCollectionInfo,
    setLoadingDocumentType,
    setDocumentTypeInfo,
    setSignatureLoading,
    setSignatureData,
    setCorporateEmail,
    setInvalidReplacementEmailInput,
    setEquipmentDescription,
    setLoadingMatrixFile,
    setSavingMatrixFile,
    setMatrixFile,
    setIsSendingStaticDocument,
    setDefaultExist,
    setPublicCertExpressToken,
    setSortColumn,
    setIsRemoveRegionReasonLoading,
    setIsSignChange,
    setAddNewSignature,
    setUserInfo,
    setIsLoadingUserInfo,
    setIsLoadingupdateRole,
    setSavedCertExpressSettings,
    setCurrentCertExpressSettings,
    setPendingCertificateEmailPreference,
    setIsLoadingNotificationPreferences,
    setEscalationEmailPreference,
    setValidationEmailPreference,
    setPreferencesLoaded,
    setPreviousPath,
} = settingsSlice.actions;

export const selectAddNewSignature = state => {
    return state.setting.addNewSignature;
};

export const selectIsSignChange = state => {
    return state.setting.isSignChange;
};

export const selectIsCompanyLogoPresent = state => {
    return state.setting.isCompanyLogoPresent;
};

export const selectIsSavingSettings = state => {
    return state.setting.isSavingSettings;
};

export const selectIsFetchingLogo = state => {
    return state.setting.isFetchingLogo;
};

export const selectIsSavingLogo = state => {
    return state.setting.isSavingLogo;
};

export const selectIsDeletingLogo = state => {
    return state.setting.isDeletingLogo;
};

export const selectCompanyLogo = state => {
    return state.setting.companyLogo;
};

export const selectIsLoading = state => {
    return state.setting.isLoading;
};

export const selectSetting = createSelector(
    state => state.setting,
    setting => {
        if (setting.settingData?.length === 0) {
            const stored = getStoredItem("generalSettings");
            if (stored) {
                return stored;
            }
        }
        return setting.settingData;
    }
);

/** Company opted in to exemption overrides (generalSettings.enableOverrides from API). */
export const selectEnableOverrides = createSelector(
    selectSetting,
    settings => settings?.enableOverrides === true
);

/** Company has the multi-tax feature enabled (generalSettings.enableMultiTax from API). */
export const selectEnableMultiTax = createSelector(
    selectSetting,
    settings => settings?.enableMultiTax === true
);

/**
 * Company opted in to checking for duplicate certificates at creation time.
 * Sourced from generalSettings.enableDuplicateCertificateCheck
 * (clients.enable_duplicate_certificate_check on the API).
 */
export const selectEnableDuplicateCertificateCheck = createSelector(
    selectSetting,
    settings => settings?.enableDuplicateCertificateCheck === true
);

/**
 * Client-level setting that allows saving email templates (e.g. cover letters)
 * which AI has flagged as spam. Sourced from generalSettings.allowUnsafeTemplateEmailSend
 * (clients.allow_unsafe_template_email_send on the API).
 */
export const selectAllowUnsafeTemplateEmailSend = createSelector(
    selectSetting,
    settings => settings?.allowUnsafeTemplateEmailSend !== false
);

export const selectCertificateRequest = state => {
    return state.setting.certificateRequest;
};

export const selectAutoRequestCoverLetter = state => {
    return state.setting.autoRequestCoverLetter;
};

export const selectIsLoadingAutoRequestCoverLetter = state => {
    return state.setting.isLoadingAutoRequestCoverLetter;
};

export const getCertificateRequest = () => (dispatch, getState) => {
    const { setting } = getState();
    return setting.certificateRequest;
};

export const selectIsLoadingCertificateRequestForm = state => {
    return state.setting.isLoadingCertificateRequestForm;
};

export const selectIsLoadingSaveMatrixSetting = state => {
    return state.setting.isLoadingSaveMatrixSetting;
};

export const selectCertificateFormsDialogOpen = state => {
    return state.setting.certificateFormsDialogOpen;
};

export const selectRequestingCertificateFormsDialogOpen = state => {
    return state.setting.requestingCertificateFormsDialogOpen;
};

export const selectPage = state => {
    return state.setting.page;
};

export const selectRequestCertificateRegions = state => {
    return state.setting.requestCertificateRegions;
};

export const selectRequestCertificateReasons = state => {
    return state.setting.requestCertificateReasons;
};

export const selectSendingCertificateRegions = state => {
    return state.setting.sendingCertificateRegions;
};

export const selectSendingCertificateReasons = state => {
    return state.setting.sendingCertificateReasons;
};
export const selectIsRequestingAvalaraDefault = state => {
    return state.setting.isRequestingAvalaraDefault;
};
export const selectIsSendingAvalaraDefault = state => {
    return state.setting.isSendingAvalaraDefault;
};

export const selectNoDataType = state => {
    return state.setting.noDataType;
};

export const selectLoadingSignature = state => {
    return state.setting.isSignatureLoading;
};

export const selectSignatureData = state => {
    return state.setting.signatureData;
};

export const selectLoadingCollectionRegions = state => {
    return state.setting.isLoadingCollectionRegions;
};

export const selectIsOutgoingMatrixSet = state => {
    return state.setting.isOutgoingMatrixSet;
};

export const selectMatrixCheckLoading = state => {
    return state.setting.matrixCheckLoading;
};

export const selectCollectionRegions = state => {
    return state.setting.collectionRegions;
};

export const selectLoadingDocumentType = state => {
    return state.setting.isLoadingDocumentType;
};

export const selectDocumentTypeInfo = state => {
    return state.setting.documentTypeInfo;
};

export const selectCollectionInfo = state => {
    return state.setting.collectionInfo;
};

export const selectIsLoadingMatrixFile = state => {
    return state.setting.isLoadingMatrixFile;
};

export const selectMatrixFile = state => {
    return state.setting.matrixFile;
};

export const selectIsSendingStaticDocument = state => {
    return state.setting.isSendingStaticDocument;
};

export const selectIsSavingMatrixFile = state => {
    return state.setting.isSavingMatrixFile;
};

export const selectDefaultExist = state => {
    return state.setting.defaultExist;
};

export const selectPublicCertExpressToken = state => {
    return state.setting.publicCertExpressToken;
};
export const selectSortColumn = state => {
    return state.setting.sortColumn;
};

export const selectIsRemoveRegionReasonLoading = state => {
    return state.setting.isRemoveRegionReasonLoading;
};

export const selectUserInfo = state => {
    return state.setting.userInfo;
};

export const selectIsLoadingUserInfo = state => {
    return state.setting.isLoadingUserInfo;
};

export const selectIsLoadingUpdateRole = state => {
    return state.setting.isLoadingupdateRole;
};

export const selectSavedCertExpressSettings = state => {
    return state.setting.certExpressSettings.savedState;
};

export const selectCurrentCertExpressSettings = state => {
    return state.setting.certExpressSettings.currentState;
};

export const selectPendingCertificateEmailPreference = state => {
    return state.setting.pendingCertificateEmailPreference;
};

export const selectIsLoadingNotificationPreferences = state => {
    return state.setting.isLoadingNotificationPreferences;
};

export const selectEscalationEmailPreference = state => {
    return state.setting.escalationEmailPreference;
};

export const selectValidationEmailPreference = state => {
    return state.setting.validationEmailPreference;
};

export const selectFaxEmails = state => {
    return state.setting.settingData.clientsFaxEmails;
};

export const selectFaxEmailsCount = state => {
    return state.setting.settingData.clientsFaxEmails.length;
};

export const selectPreviousPath = state => {
    return state.setting.previousPath;
};

export const validateSettings = settingId => (dispatch, getState) => {
    const { setting } = getState();
    return setting.settingData[settingId];
};

export const fetchCertificateRequestsAPI = () => async (dispatch, getState) => {
    const { setting } = getState();
    if (setting.isLoadingCertificateRequest) {
        return;
    }
    dispatch(setIsLoadingCertificateRequest(true));
    const response = await axios.get(buildApiV3Url(`certificateRequestSettings`), {
        withCredentials: true,
    });
    if (response && response.data) {
        dispatch(setCertificateRequest(response.data));
    } else {
        dispatch(setCertificateRequest([]));
    }
    dispatch(setIsLoadingCertificateRequest(false));
    return response?.data;
};

export const fetchAutoRequestCoverLetter = clientId => async (dispatch, getState) => {
    const { setting, session } = getState();
    const companyId = session?.activeCompany?.id;

    if (setting.isLoadingAutoRequestCoverLetter || !companyId || !clientId) {
        return null;
    }

    dispatch(setIsLoadingAutoRequestCoverLetter(true));
    try {
        const response = await axios.get(
            buildApiV3Url(`companies/${companyId}/auto-request-cover-letters/${clientId}`),
            {
                withCredentials: true,
            }
        );

        const autoRequestCoverLetter = response?.data ?? null;
        dispatch(setAutoRequestCoverLetter(autoRequestCoverLetter));

        return autoRequestCoverLetter;
    } catch (error) {
        dispatch(setAutoRequestCoverLetter(null));
        throw error;
    } finally {
        dispatch(setIsLoadingAutoRequestCoverLetter(false));
    }
};

export const getCertificateRequestSettingsIfNeeded = () => async (dispatch, getState) => {
    const { certificateRequest } = getState().setting;
    if (
        certificateRequest &&
        !Array.isArray(certificateRequest) &&
        Array.isArray(certificateRequest.webPortalOptions)
    ) {
        return certificateRequest;
    }
    return dispatch(fetchCertificateRequestsAPI());
};

export const clearCollectionData = () => async dispatch => {
    dispatch(setCollectionRegions({}));
};

export const fetchCertificateRequestFormsAPI =
    (outgoing = false, forceAll = false, pageSize, pageNumber = 0, onLanding, paginateDetails) =>
    async (dispatch, getState) => {
        const { setting } = getState();
        const { rowsPerPage } = getState().session;
        pageSize = !pageSize ? rowsPerPage : pageSize;
        if (setting.isLoadingCertificateRequestForm) {
            return;
        }
        const reasonName = outgoing
            ? setting.sendingCertificateReasons.reasonName
            : setting.requestCertificateReasons.reasonName;
        const regionName = outgoing
            ? setting.sendingCertificateRegions.regionName
            : setting.requestCertificateRegions.regionName;
        const isAvalaraDefault = outgoing
            ? setting.isSendingAvalaraDefault
            : setting.isRequestingAvalaraDefault;
        const reasonId = outgoing
            ? setting.sendingCertificateReasons.reasonId
            : setting.requestCertificateReasons.reasonId;
        let filterstring = `region.documentType.name eq 'Sales and Use Tax' and region.documentType.outgoing eq ${outgoing}`;
        if (reasonId) filterstring += ` and exemptReason.Id eq "${reasonId}"`;
        else if (reasonName) filterstring += ` and exemptReason.Name eq "${reasonName}"`;
        if (regionName) filterstring += ` and region.Name eq "${regionName}"`;
        if (isAvalaraDefault && !forceAll) {
            filterstring += ` and setToDefault eq "${!isAvalaraDefault}"`;
        }
        const sort = `${setting.sortColumn[0]} ${setting.sortColumn[1] ? "ASC" : "DESC"}`;

        dispatch(setIsLoadingCertificateRequestForm(true));
        const apiURI = buildApiV3UrlWithQuery(
            `exemption-matrix/default-matrix`,
            filterstring,
            "",
            pageSize,
            pageNumber,
            sort
        );

        const apiResponse = await axios.get(`${apiURI}`, {
            withCredentials: true,
        });

        if (errorResponse(apiResponse)?.code) {
            onLanding ? dispatch(setNoDataType("onLanding")) : dispatch(setNoDataType("onSearch"));
            dispatch(setPageToNull(pageSize));
        } else if (apiResponse?.data) {
            dispatch(setNoDataType(""));
            const response = transformExemptionData(apiResponse, false, false, [
                "exemptReasonName",
            ]);

            const paginateData = paginateDetails
                ? {
                      pageNumber: paginateDetails.currentPage,
                      startIndex: paginateDetails.startIndex,
                      endIndex: paginateDetails.endIndex,
                      rowsPerPage: paginateDetails.rowsPerPage,
                      totalRecords: response?.data?.count,
                  }
                : {
                      pageNumber: 1,
                      startIndex: 0,
                      endIndex: 100,
                      rowsPerPage: 100,
                      totalRecords: response?.data?.count,
                  };
            dispatch(
                setPage({
                    data: response?.data?.value,
                    paginate: paginateData,
                })
            );
        } else {
            dispatch(setPageToNull([]));
        }
        dispatch(setIsLoadingCertificateRequestForm(false));
    };

export const fetchOutgoingMatrixSet = () => async (dispatch, getState) => {
    const { setting } = getState();
    if (setting.setMatrixCheckLoading) {
        return;
    }
    const pageSize = 1;
    const pageNumber = 0;
    const filterstring = `region.documentType.name eq 'Sales and Use Tax' and region.documentType.outgoing eq true`;

    dispatch(setMatrixCheckLoading(true));
    const collectionURI = buildApiV3UrlWithQuery(
        `exemption-matrix/default-matrix`,
        filterstring,
        "",
        pageSize,
        pageNumber,
        ""
    );

    const collectionResponse = await axios.get(`${collectionURI}`, {
        withCredentials: true,
    });

    if (
        collectionResponse &&
        collectionResponse?.data &&
        !errorResponse(collectionResponse)?.code
    ) {
        dispatch(setIsOutgoingMatrixSet(true));
    } else {
        dispatch(setIsOutgoingMatrixSet(false));
    }
    dispatch(setMatrixCheckLoading(false));
};

export const fetchCollectionRegionsAPI =
    (outgoing = false, taxCode) =>
    async (dispatch, getState) => {
        const { setting } = getState();
        if (setting.isLoadingCollectionRegions) {
            return;
        }
        dispatch(clearCollectionData());

        const pageSize = 1000;
        const pageNumber = 0;
        const filterstring = `region.documentType.name eq 'Sales and Use Tax' and region.documentType.outgoing eq ${outgoing} and exemptReason.Id eq '${taxCode}'`;

        dispatch(setIsLoadingCollectionRegion(true));
        const collectionURI = buildApiV3UrlWithQuery(
            `exemption-matrix/default-matrix`,
            filterstring,
            "",
            pageSize,
            pageNumber,
            ""
        );

        const collectionResponse = await axios.get(`${collectionURI}`, {
            withCredentials: true,
        });

        if (collectionResponse && collectionResponse?.data) {
            const regions = collectionResponse.data.value;
            const optionsList = [];
            const infoList = [];
            forIn(regions, (value, key) => {
                infoList.push({
                    collectionId: regions[key].id,
                    regionName: regions[key].regionName,
                    reasonName: regions[key].exemptReasonName,
                });
                optionsList.push(regions[key].regionName);
            });
            dispatch(setCollectionRegions(optionsList));
            dispatch(setCollectionInfo(infoList));
        } else {
            dispatch(setCollectionRegions({}));
        }
        dispatch(setIsLoadingCollectionRegion(false));
    };

const [showToast, hideToast] = toast();

export const fetchSettingsAPI = forceApi => async (dispatch, getState) => {
    const { setting } = getState();
    if (setting.isSavingSettings) {
        return false;
    }

    if (!forceApi) {
        const stored = getStoredItem("generalSettings");
        if (stored) {
            dispatch(setSetting(stored));
            return false;
        }
    }

    dispatch(setIsSavingSettings(true));
    try {
        const response = await axios.get(buildApiV3Url(`generalSettings`), {
            withCredentials: true,
        });
        if (response && response.data) {
            if (!Number.isNaN(response.data.autoSendExpiring)) {
                response.data.autoSendExpiring = response.data.autoSendExpiring.toString();
            }

            if (
                !Number.isNaN(response.data.autoRequestInterval) &&
                response.data.autoRequestInterval > 0
            ) {
                response.data.autoRequestInterval = response.data.autoRequestInterval.toString();
            }

            if (!Number.isNaN(response.data.autoRequestMax) && response.data.autoRequestMax > 0) {
                response.data.autoRequestMax = response.data.autoRequestMax.toString();
            }

            dispatch(setSetting(response.data));
            if (response?.data?.auiWorkflowFlags) {
                dispatch(setAuiWorkflowData(response.data.auiWorkflowFlags));
            }
        }
    } catch (error) {
        const errorMessage = error?.response?.data?.[0]?.message || "Failed to refresh settings";
        showToast("error", errorMessage);
        return false;
    } finally {
        dispatch(setIsSavingSettings(false));
    }
    return true;
};

/**
 * Enables REST API for the company when usesJobs, enableLocalJurisdiction or enableMultiTax is turned on.
 * This is called in parallel with the settings save and does not block the main flow.
 */
const enableRestApiForCompany = async () => {
    try {
        await axios.post(buildApiV3Url(`externalapis/enable-rest-api`), "", {
            withCredentials: true,
        });
    } catch (error) {
        // Log error but don't affect the main save flow
        await logger(1, "error", { error: error.message });
    }
};

export const putGeneralSettings = putData => async (dispatch, getState) => {
    const { setting } = getState();
    dispatch(setIsSavingSettings(true));

    // Check if usesJobs, enableLocalJurisdiction or enableMultiTax changed from false to true
    const usesJobsTurnedOn = !setting.settingData.usesJobs && putData.usesJobs;
    const enableLocalJurisdictionTurnedOn =
        !setting.settingData.enableLocalJurisdiction && putData.enableLocalJurisdiction;
    const enableMultiTaxTurnedOn = !setting.settingData.enableMultiTax && putData.enableMultiTax;

    // Call enable REST API in parallel if any of these settings was turned on
    if (usesJobsTurnedOn || enableLocalJurisdictionTurnedOn || enableMultiTaxTurnedOn) {
        enableRestApiForCompany();
    }

    try {
        // Save general settings
        await axios.put(
            buildApiV3Url(`generalSettings`),
            { ...setting.settingData, ...putData },
            { withCredentials: true }
        );
        dispatch(setIsSavingSettings(false));
        showToast("success", `Settings saved successfully`);
        await dispatch(fetchSettingsAPI(true));
        dispatch(updateAuiWorkflowTask(AuiWorkflowTaskCategory.SETTINGS));
    } catch (error) {
        dispatch(setIsSavingSettings(false));
        showToast("error", "Failed to save settings");
        throw error;
    }
};

export const putCertificateRequestSettings = data => async () => {
    const putData = {
        gencertRegistration: data.gencertRegistration,
        webPortalOptions: data.webPortalOptions,
    };
    await axios
        .put(buildApiV3Url(`certificateRequestSettings`), putData, {
            withCredentials: true,
        })
        .then(() => {
            showToast("success", `Certificate request settings saved successfully`);
        });
};

export const putAutoRequestCoverLetter = (clientId, putData) => async (dispatch, getState) => {
    const companyId = getState().session?.activeCompany?.id;
    if (!companyId || !clientId) {
        return null;
    }

    const payload = {
        coverLetterId: parseInt(putData.coverLetterId, 10),
        documentTypeId: parseInt(putData.documentTypeId, 10),
    };

    const response = await axios.put(
        buildApiV3Url(`companies/${companyId}/auto-request-cover-letters/${clientId}`),
        payload,
        {
            withCredentials: true,
        }
    );

    dispatch(setAutoRequestCoverLetter(payload));
    return response;
};

export const fetchCompanyLogo = () => async (dispatch, getState) => {
    const { setting } = getState();
    if (setting.isFetchingLogo) {
        return;
    }
    dispatch(setIsFetchingLogo(true));
    const response = await axios.get(buildApiV3Url(`logo`), {
        responseType: "arraybuffer",
        withCredentials: true,
    });
    if (response.status === 204) {
        dispatch(setIsCompanyLogoPresent(false));
    }
    if (response && response.data && response.status === 200) {
        dispatch(setIsCompanyLogoPresent(true));
        const buffer = Buffer.from(response.data, "binary").toString("base64");
        dispatch(
            setCompanyLogo({
                fileType: `${response.headers["content-type"]}`,
                data: buffer,
                fileName: "",
            })
        );
    }
    dispatch(setIsFetchingLogo(false));
};

export const saveCompanyLogo = fileData => async (dispatch, getState) => {
    const { setting } = getState();
    if (setting.isSavingLogo) {
        return;
    }
    dispatch(setIsSavingLogo(true));
    await axios.post(buildApiV3Url(`logo`), fileData, { withCredentials: true }).then(() => {
        showToast("success", `Company logo saved successfully`);
        dispatch(fetchCompanyLogo());
    });
    dispatch(setIsSavingLogo(false));
};

export const deleteCompanyLogo = () => async dispatch => {
    dispatch(setIsDeletingLogo(true));
    await axios
        .delete(buildApiV3Url(`logo`), {
            withCredentials: true,
        })
        .then(() => {
            showToast("success", `Company Logo deleted successfully`);
            dispatch(setIsCompanyLogoPresent(false));
        });
    dispatch(setIsDeletingLogo(false));
};

export const putDefaultMatrixSetting =
    (matrixSetting = null) =>
    async (dispatch, getState) => {
        const { setting, isLoadingSaveMatrixSetting } = getState();
        const matrixData = matrixSetting || setting?.matrixSetting;
        if (isLoadingSaveMatrixSetting) return;
        dispatch(setIsLoadingSaveMatrixSetting(true));
        await axios
            .put(buildApiV3Url(`exemption-matrix/default-matrix/${matrixData.id}`), matrixData, {
                withCredentials: true,
            })
            .then(() => {
                showToast(
                    "success",
                    `Default matrix settings updated successfully`,
                    "save-form-success-toast"
                );
                dispatch(fetchCertificateRequestFormsAPI(matrixData.outgoing));
            });
        dispatch(setIsLoadingSaveMatrixSetting(false));
    };

export const putResetDefaultMatrixSetting = () => async (dispatch, getState) => {
    const { setting, isLoadingSaveMatrixSetting } = getState();
    const resetMatrixData = setting?.resetMatrixSetting;
    if (isLoadingSaveMatrixSetting) return;
    dispatch(setIsLoadingSaveMatrixSetting(true));
    await axios
        .put(
            buildApiV3Url(`exemption-matrix/reset-default-matrix/${resetMatrixData.id}`),
            resetMatrixData,
            { withCredentials: true }
        )
        .then(() => {
            showToast("success", `Default matrix settings reset successfully`);
            dispatch(fetchCertificateRequestFormsAPI());
        });
    dispatch(setIsLoadingSaveMatrixSetting(false));
};

export const fetchSignatureData = companyEntityId => async (dispatch, getState) => {
    const {
        setting: { isSignatureLoading },
    } = getState();
    if (isSignatureLoading) {
        return;
    }

    dispatch(setSignatureLoading(true));

    const response = await axios.get(buildApiV3Url(`account-signatures/${companyEntityId}`), {
        withCredentials: true,
    });
    if (response && response.data && response.status === 200) {
        if (errorResponse(response)?.code.toLowerCase() === "objectnotfound") {
            dispatch(setSignatureData(null));
        } else {
            dispatch(setSignatureData(response.data));
        }
    }
    dispatch(setSignatureLoading(false));
};

export const saveSignatureData = sigPostData => async dispatch => {
    dispatch(setSignatureLoading(true));

    await axios.post(buildApiV3Url(`account-signatures`), sigPostData, {
        withCredentials: true,
    });

    dispatch(setSignatureLoading(false));
};

export const updateSignatureData = sigPutData => async dispatch => {
    dispatch(setSignatureLoading(true));
    await axios.put(buildApiV3Url(`account-signatures/${sigPutData.Id}`), sigPutData, {
        withCredentials: true,
    });

    dispatch(setSignatureLoading(false));
};

export const deleteSignatureDataById = signatureId => async dispatch => {
    dispatch(setSignatureLoading(true));
    await axios.delete(buildApiV3Url(`account-signatures/${signatureId}`), {
        withCredentials: true,
    });

    dispatch(setSignatureLoading(false));
};

export const getMatrixFile = matrixId => async (dispatch, getState) => {
    const { isLoadingMatrixFile } = getState();
    if (isLoadingMatrixFile) {
        return;
    }
    dispatch(setLoadingMatrixFile(true));
    const response = await axios.get(buildApiV3Url(`exemption-matrix/matrix-file/${matrixId}`), {
        withCredentials: true,
        responseType: "arraybuffer",
    });
    if (response.status === 204) {
        dispatch(setLoadingMatrixFile(false));
        dispatch(setMatrixFile(""));
    } else if (response && response.data) {
        const buffer = Buffer.from(response.data, "binary").toString("base64");
        dispatch(setMatrixFile(`data:${response.headers["content-type"]};base64,${buffer}`));
        dispatch(setLoadingMatrixFile(false));
    }
};

export const postMatrixFileAsync = (matrixId, fileData) => async dispatch => {
    await axios
        .post(buildApiV3Url(`exemption-matrix/${matrixId}/matrix-file`), fileData, {
            withCredentials: true,
        })
        .then(() => {
            dispatch(getMatrixFile(matrixId));
            dispatch(setSavingMatrixFile(false));
        });
};

export const sendOutgoingStaticDocument =
    (requestData, navigateToVendors) => async (dispatch, getState) => {
        const { setting, session } = getState();
        if (setting.isSendingStaticDocument) {
            return;
        }
        dispatch(setIsSendingStaticDocument(true));

        const ecmRole = session.ecmRole;
        const sentByMs = ecmRole?.includes(EcmRole.MSUser) || false;
        const updatedRequestData = { ...requestData, SentByMs: sentByMs };

        try {
            await axios.post(buildApiV3Url(`outgoing-requests`), updatedRequestData, {
                withCredentials: true,
            });

            const isEmailDelivery = requestData.DeliveryMethod === "E";
            const successMessage = isEmailDelivery
                ? `Document request sent. We emailed ${requestData.EmailTo}`
                : "We're creating your request. It may take a few minutes.";
            const actionText = isEmailDelivery
                ? "Go back to my vendors"
                : "View the request file in My Downloads";
            const actionCallback = isEmailDelivery
                ? () => {
                      hideToast("outgoing-request-success");
                      navigateToVendors();
                  }
                : () => {
                      hideToast("download-success");
                      window.open(
                          `https://${getConfig().hostnames.docLockerClient}/mylockers/downloads`,
                          "_blank",
                          "noopener,noreferrer"
                      );
                  };

            showToast(
                "success",
                successMessage,
                isEmailDelivery ? "outgoing-request-success" : "download-success",
                true,
                actionText,
                actionCallback
            );
        } catch (error) {
            await logger(1, "error", { error: error.message });
        }
        dispatch(setIsSendingStaticDocument(false));
    };

export const checkIfSystemCollection = (reasonTag, regionTag) => async dispatch => {
    dispatch(setDefaultExist(false));
    const pageSize = 10;
    const pageNumber = 0;
    const filterString = `exposureZoneTag eq '${regionTag}' AND exemptReasonTag eq '${reasonTag}'`;
    const systemMatrixURI = buildApiV3UrlWithQuery(
        `exemption-matrix/system-default-matrix-expire`,
        filterString,
        "",
        pageSize,
        pageNumber,
        ""
    );

    const matrixResponse = await axios.get(`${systemMatrixURI}`, {
        withCredentials: true,
    });

    if (errorResponse(matrixResponse)?.code) {
        dispatch(setDefaultExist(false));
    } else {
        dispatch(setDefaultExist(true));
    }
};

export const fetchPublicCertExpressToken = clientId => async dispatch => {
    const tokenURI = buildApiV2Point5UrlWithQuery(
        `get-public-certexpress-token`,
        `ClientID eq ${clientId}`,
        null,
        null,
        null,
        null
    );

    const tokenResponse = await axios.get(tokenURI, {
        withCredentials: true,
    });

    dispatch(
        setPublicCertExpressToken(
            `${getConfig().hostnames.certExpress}/?c=${tokenResponse.data.token}:${tokenResponse.data.iv}`
        )
    );
};

export const fetchDocumentTypeInfoByName = (docName, outgoing) => async (dispatch, getState) => {
    const { setting } = getState();
    if (setting.isLoadingDocumentType) {
        return false;
    }
    dispatch(setLoadingDocumentType(true));
    const docURI = buildApiV3Url(
        `document-types?$filter=name eq '${docName}' and outgoing eq '${outgoing}'`
    );
    const response = await axios.get(docURI, {
        withCredentials: true,
    });
    dispatch(setLoadingDocumentType(false));
    dispatch(setDocumentTypeInfo(response.data.value));
    return true;
};

export const removeRegionAndReason = () => async (dispatch, getState) => {
    const { setting, isRemoveRegionReasonLoading } = getState();
    const matrixData = setting?.matrixSetting;
    const regionName = matrixData?.regionName;
    const reasonName = matrixData?.reasonName;
    const body = { regionName, reasonName };

    if (isRemoveRegionReasonLoading) return;
    dispatch(setIsRemoveRegionReasonLoading(true));
    await axios
        .delete(buildApiV3Url(`exemption-matrix/${matrixData?.id}`), {
            data: body,
            withCredentials: true,
        })
        .then(() => {
            showToast("success", `Region and reason deleted successfully`);
        });
    dispatch(setIsRemoveRegionReasonLoading(false));
    dispatch(fetchCertificateRequestFormsAPI(false));
};

export const fetchDocumentTypeInfoType = isOutgoing => async (dispatch, getState) => {
    const { setting } = getState();
    if (setting.isLoadingDocumentType) {
        return false;
    }

    dispatch(setLoadingDocumentType(true));
    const docURI = buildApiV3Url(
        `document-types?$filter=name eq  'Sales and Use Tax' and outgoing eq ${isOutgoing}`
    );
    const response = await axios.get(docURI, {
        withCredentials: true,
    });

    dispatch(setDocumentTypeInfo(response.data.value));
    dispatch(setLoadingDocumentType(false));
    return true;
};

export const reconcileDocuments = () => async () => {
    const response = await axios.post(
        buildApiV3Url(`reconcile-documents`),
        {},
        { withCredentials: true }
    );

    if (response && response.data) {
        if (response.data.success) {
            showToast("success", `Rebuild request submitted.`);
        } else {
            showToast("error", `The rebuild service is unavailable. Try again later.`);
        }
    }
};

export const getUserInfo = userId => async (dispatch, getState) => {
    const { isLoadingUserInfo } = getState().setting;
    if (isLoadingUserInfo) {
        return false;
    }
    dispatch(setIsLoadingUserInfo(true));
    try {
        const response = await axiosInstanceWithoutInterceptor.get(
            buildApiV3Url(`user-role/${userId}`),
            { withCredentials: true }
        );

        if (response?.status === 200 && response?.data) {
            dispatch(setUserInfo(response?.data));
        }
    } catch {
        dispatch(setIsLoadingUserInfo(false));
        return false;
    }
    dispatch(setIsLoadingUserInfo(false));
    return true;
};

export const updateMSUserRole = userId => async (dispatch, getState) => {
    const localState = getState();
    const { activeCompany } = localState.session;
    const { userInfo, isLoadingupdateRole } = localState.setting;
    if (isLoadingupdateRole) return;
    dispatch(setIsLoadingupdateRole(true));

    const msUserRolePayload = {
        userRoles: [
            {
                role: EcmRole.MSUser,
                companyIds: [activeCompany.id],
                isAllCompany: true,
            },
        ],
    };

    try {
        if (!userInfo?.subjectId) {
            showToast("error", "Unable to update managed services user role");
            return;
        }

        const userRoleUrl = buildApiV3Url(
            `user-role?subjectId=${userInfo.subjectId}&userId=${userId}`
        );
        const hasExistingUserRoles =
            Array.isArray(userInfo.userRoles) && userInfo.userRoles.length > 0;

        const response = hasExistingUserRoles
            ? await axiosInstanceWithoutInterceptor.put(userRoleUrl, msUserRolePayload, {
                  withCredentials: true,
              })
            : await axiosInstanceWithoutInterceptor.post(userRoleUrl, msUserRolePayload, {
                  withCredentials: true,
              });

        if (response.status >= 200 && response.status < 300) {
            dispatch(getUserInfo(userId));
            showToast("success", "Managed services user role has been assigned successfully");
        } else {
            showToast("error", "Unable to update managed services user role");
        }
    } catch {
        showToast("error", "Unable to update managed services user role");
    } finally {
        dispatch(setIsLoadingupdateRole(false));
    }
};

export const deleteMSUserRole = userId => async (dispatch, getState) => {
    const localState = getState();
    const { userInfo, isLoadingupdateRole } = localState.setting;
    if (isLoadingupdateRole) return;
    dispatch(setIsLoadingupdateRole(true));

    axiosInstanceWithoutInterceptor
        .delete(buildApiV3Url(`user-role?subjectId=${userInfo?.subjectId}`), {
            withCredentials: true,
        })
        .then(() => {
            dispatch(getUserInfo(userId));
            showToast("success", `Managed services user role has been unassigned successfully`);
        });
    dispatch(setIsLoadingupdateRole(false));
};

export const deleteFaxEmailAddress = faxEmailAddress => async (dispatch, getState) => {
    const { setting } = getState();
    if (!faxEmailAddress || !faxEmailAddress.trim() || !validateEmail(faxEmailAddress)) {
        showToast("error", "Invalid email address selected");
        return;
    }
    try {
        const response = await axios.delete(buildApiV3Url(`fax-emails`), {
            data: {
                faxEmailAddress: faxEmailAddress,
            },
            withCredentials: true,
        });
        if (response.status === 200 && response.data.success === true) {
            dispatch(
                setSetting({
                    ...setting.settingData,
                    clientsFaxEmails: setting.settingData.clientsFaxEmails.filter(
                        email => email.faxEmailAddress !== faxEmailAddress
                    ),
                })
            );
            showToast("success", "Client Fax Email has been deleted");
            return true;
        } else {
            showToast("error", response?.data?.message);
        }
    } catch (error) {
        const errorMessage =
            error?.response?.data?.[0]?.message || "Failed to delete client fax email";
        showToast("error", errorMessage);
    }
    return false;
};

export const addClientFaxEmail = payload => async dispatch => {
    try {
        const response = await axios.post(buildApiV3Url(`fax-emails`), payload, {
            withCredentials: true,
        });

        if (response.data.success === true) {
            showToast("success", "Email saved successfully");
            await dispatch(fetchSettingsAPI(true));
            return true;
        } else {
            showToast("error", response?.data?.message);
        }
    } catch (error) {
        const errorMessage =
            error?.response?.data?.[0]?.message || "Failed to add client fax email";
        showToast("error", errorMessage);
    }
    return false;
};

export const updateClientFaxEmail = payload => async dispatch => {
    try {
        const response = await axios.put(buildApiV3Url(`fax-emails`), payload, {
            withCredentials: true,
        });

        if (response.data.success === true) {
            showToast("success", "Email saved successfully");
            await dispatch(fetchSettingsAPI(true));
            return true;
        } else {
            showToast("error", response?.data?.message);
        }
    } catch (error) {
        const errorMessage =
            error?.response?.data?.[0]?.message || "Failed to update client fax email";
        showToast("error", errorMessage);
    }
    return false;
};

const parsePrefValue = (raw, label, fallback) => {
    try {
        const parsed = JSON.parse(raw);
        return { enabled: parsed.enabled || false, email: parsed.email || "" };
    } catch (parseError) {
        void logger(2, "error", {
            message: `Failed to parse ${label} preference value: ${parseError?.message}`,
        });
        return fallback;
    }
};

const applyUserPreferences = (prefs, dispatch) => {
    const prefMap = [
        {
            key: PREF_KEY_PENDING_CERTIFICATE_EMAIL,
            label: "pending certificate",
            action: setPendingCertificateEmailPreference,
            fallback: initialState.pendingCertificateEmailPreference,
        },
        {
            key: PREF_KEY_OPT_IN_ESCALATED_NOTIFICATIONS,
            label: "escalation",
            action: setEscalationEmailPreference,
            fallback: initialState.escalationEmailPreference,
        },
        {
            key: PREF_KEY_OPT_IN_VALIDATION_NOTIFICATIONS,
            label: "validation",
            action: setValidationEmailPreference,
            fallback: initialState.validationEmailPreference,
        },
    ];

    for (const { key, label, action, fallback } of prefMap) {
        const pref = prefs.find(p => p.preference === key);
        if (pref?.preferenceValue) {
            dispatch(action(parsePrefValue(pref.preferenceValue, label, fallback)));
        }
    }
};

export const fetchUserPreferences = () => async (dispatch, getState) => {
    const { setting } = getState();

    if (setting.preferencesLoaded) {
        return;
    }

    if (setting.isLoadingNotificationPreferences) {
        return;
    }

    dispatch(setIsLoadingNotificationPreferences(true));

    try {
        const preferenceResponse = await axios.get(buildApiV3Url("ecm-preferences"), {
            withCredentials: true,
        });

        if (preferenceResponse?.data?.value) {
            applyUserPreferences(preferenceResponse.data.value, dispatch);
        }
        dispatch(setPreferencesLoaded(true));
    } catch (error) {
        void logger(2, "error", { message: `Failed to fetch user preferences: ${error?.message}` });
        dispatch(
            setPendingCertificateEmailPreference(initialState.pendingCertificateEmailPreference)
        );
        dispatch(setEscalationEmailPreference(initialState.escalationEmailPreference));
        dispatch(setValidationEmailPreference(initialState.validationEmailPreference));
        dispatch(setPreferencesLoaded(false));
        showToast("error", "Failed to fetch preferences");
    } finally {
        dispatch(setIsLoadingNotificationPreferences(false));
    }
};

export const postUserPreferences =
    (pendingCertEnabled, escalationEnabled, validationEnabled, email) => async dispatch => {
        const resolvedEmail = email || "";
        const preferences = [
            {
                key: PREF_KEY_PENDING_CERTIFICATE_EMAIL,
                enabled: pendingCertEnabled,
                action: setPendingCertificateEmailPreference,
            },
            {
                key: PREF_KEY_OPT_IN_ESCALATED_NOTIFICATIONS,
                enabled: escalationEnabled,
                action: setEscalationEmailPreference,
            },
            {
                key: PREF_KEY_OPT_IN_VALIDATION_NOTIFICATIONS,
                enabled: validationEnabled,
                action: setValidationEmailPreference,
            },
        ];

        const commands = preferences.map(({ key, enabled }) => ({
            preference: key,
            preferenceValue: JSON.stringify({ enabled, email: resolvedEmail }),
        }));

        try {
            await axios.post(buildApiV3Url("ecm-preferences-batch"), commands, {
                withCredentials: true,
            });
            preferences.forEach(({ enabled, action }) =>
                dispatch(action({ enabled, email: resolvedEmail }))
            );
            showToast("success", "Preferences saved successfully");
            return true;
        } catch (error) {
            void logger(2, "error", {
                message: `Failed to save user preferences: ${error?.message}`,
            });
            showToast("error", "Failed to save preferences");
            return false;
        }
    };

export default settingsSlice.reducer;
